import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import ApplicationContext from '../../../ApplicationContext';
import { ApiResult, InsertResultsWithData } from '../../../framework.core/data/ApiResult';
import {JobNames} from '../../../framework.core/JobNames';
import useDirtyCheck from '../../../hooks/useDirtyCheck2';
import MawbMilestoneView from './implementation/MawbMilestone';
import { useTypedFastStorage } from '../../../hooks/useFastStorage';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import { TrackableEntity } from '../../../fms.core/data/ChangeTrackable';
import { getSQLInfoList } from '../../../fms.core/data/TrackingModule';
import {AeMbl} from "../../../fms.core/AeMbl";
import SecondaryButton from '../../../ui/SecondaryButton';
import { AiAirLineEvents, AirMblStatusMap, MblStatusViewModel } from '../../../fms.core/MblStatusViewModel';
import { prepareAiMawbModelForView } from '../../airImport/mawb/util';
import { CargoEventName } from '../../../framework.core/CargoEventName';
import { CargoEventLog } from '../../../fms.core/CargoEventLog';
import { SQLInfo } from '../../../framework.core/data/SQLInfo';
import { AddEventToNotice, CreateCargoEventLog, FindIndexOfKeyStartsWith } from '../../oceanImport/mbl/implementation/mblUtils';
import { formatDateTime } from '../../../dateUtils';
import constants from '../../../constants';

const eventMappings = [
  { prefix: "0|", name: CargoEventName.FullIn, seq: 0, mappingKey:'0' },
  { prefix: "1|", name: CargoEventName.VesselDeparted, seq: 1, mappingKey:'1' },
  { prefix: "2|", name: CargoEventName.CustomsRls, seq: 2, mappingKey:'2' },
  { prefix: "3|", name: CargoEventName.VesselArrived, seq: 3, mappingKey:'3' },
  { prefix: "4|", name: CargoEventName.PickAvail, seq: 4, mappingKey:'4' },
  { prefix: "5|", name: CargoEventName.Delivered, seq: 5, mappingKey:'5' },
];

function MawbMilestoneImpl({
  mawbId,
  storageKey,
  onClose,
}: {
  mawbId: number,
  storageKey: string,
  onClose: VoidFunction,
}) {
  const appContext = useContext(ApplicationContext);

  const [showGlobalOkDialog] = useGlobalOkDialog();

  const [initialized, setInitialized] = useState(false);

  const [mawb, setMawb, removeMawb, mawbRef] = useTypedFastStorage<AeMbl>(storageKey, null);
  const [/*blStatus*/, /*setBlStatus*/, removeBlStatus, /*blStatusRef*/] = useTypedFastStorage<MblStatusViewModel>(storageKey, null);
  const {setCleanState, } = useDirtyCheck(storageKey, ['updated', 'updatedBy']);

  useEffect(() => {
    initialize();
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function doClose() {
    cleanup();
    onClose();
  }

  function cleanup() {
    removeMawb();
    removeBlStatus();
  }

  async function initialize() {
    // Load MAWB
    const apiResult:ApiResult<AeMbl> = await appContext.api.getJobDetail(JobNames.AEMbl, { id: mawbId});
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog('Cannot load data');
      doClose();
      return;
    }

    const loadedMawb = new AeMbl(apiResult.result);

    const blApiResult:ApiResult<MblStatusViewModel> = await appContext.api.getMblStatusViewModel(JobNames.AEMbl, mawbId);
    loadedMawb.milestone = new MblStatusViewModel(blApiResult?.result ?? null)
    prepareAiMawbModelForView(loadedMawb);

    const loadedModelObj = new AeMbl(_.cloneDeep(loadedMawb));
    setCleanState(loadedModelObj);
    setMawb(loadedModelObj);
    
    if(mawbRef.current !== null) {
      mawbRef.current.trackable = true;
    }

    setInitialized(true);
  }

  async function save() {
    if (!mawb || !mawbRef.current) {
      throw new Error('Invalid operation');
    }

    if(!mawbRef.current.isDirty) {
      //await showGlobalOkDialog(<p>Nothing to save!!!</p>);
      doClose();
      return;
    }
    
    const blStatusInfoList = getSQLInfoList(mawb as TrackableEntity);

    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveMblStatusViewModel(blStatusInfoList);

    if (apiResult && apiResult.success) {
      await UpdateLogMawbMilestone(blStatusInfoList,mawb);    //[t49-webhook] LogMblMilestone. 이 기능은 TrackingService를 사용하지 않더라도 적용한다.
      await showGlobalOkDialog(<p>The data has been saved</p>);
    } else {
      await showGlobalOkDialog(<div>
        <p>Cannot save</p>
        <p>{apiResult?.message}</p>
      </div>);
    }

    doClose();
  }

  async function UpdateLogMawbMilestone(blStatusInfoList: SQLInfo[], mawb:AeMbl) {
    const cntrDataWithSeq: {
      loc: string,
      cntrNo: string,
      seq: number,
    }[] = []

    const dicEvent = CollectMawbEvents(blStatusInfoList, cntrDataWithSeq);
    if (dicEvent.length > 0) {
      const cmile: CargoEventLog[] = [];
      const lastEvents: CargoEventLog[] = [];

      for (const mapping of eventMappings) {
        const dindex = FindIndexOfKeyStartsWith(dicEvent, mapping.prefix);
        if (dindex >= 0) {
          const cargoEventLog = CreateCargoEventLog(mawb.mawbNo, mapping.name, dicEvent[dindex], cntrDataWithSeq.filter(x => x.seq === mapping.seq), appContext.user?.id ?? '');
          cmile.push(...cargoEventLog);
        }
      }

      const uniqueCmileSet = new Set(cmile.map(item => item.cntrNo));
      Array.from(uniqueCmileSet).forEach(x => {
        const tempData = cmile.filter(c => c.cntrNo === x).sort((a, b) => new Date(a.utcTime ?? '').getTime() - new Date(b.utcTime ?? '').getTime());
        lastEvents.push(tempData[tempData.length - 1]);
      });

      if (lastEvents.length > 0) {
        const apiResult = await appContext.api.trackingLogMblEvents(JobNames.AEMbl, mawbId, lastEvents);;
        if (!apiResult || !apiResult.success) {
          await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
        }
      }
    }
  }

  function CollectMawbEvents(blStatusInfoList: SQLInfo[], cntrDataWithSeq: { loc: string, cntrNo: string, seq: number }[]) {
    const changeData: { pimsCntrId: number, seqNum: number, dateTime: string }[] = [];
    const dicEvent: { key: string, value: string }[] = [];

    AiAirLineEvents.forEach((enumEvent) => {
      blStatusInfoList.forEach(x => {
        const findData = x.fields.find(x => x.propertyName === AirMblStatusMap.get(enumEvent)?.cntrDate);
        if (findData) {
          const pimsCntrId = mawb?.milestone?.eventItemList[0].pimsCntrId ?? 0;
          changeData.push({ pimsCntrId: pimsCntrId, seqNum: enumEvent, dateTime: formatDateTime(findData.value) });
          const loc = x.fields.find(x => x.propertyName === AirMblStatusMap.get(enumEvent)?.cntrLoc);

          cntrDataWithSeq.push({ loc: loc?.value, seq: enumEvent, cntrNo: '' })
        }
      })
    });
    changeData.forEach(x => {
      const seqNum = x.seqNum;
      const mappingKey = eventMappings.find(x => x.seq === seqNum)?.mappingKey;
      if (mappingKey)
        AddEventToNotice(dicEvent, `${mappingKey}|${x.dateTime}`, String(x.pimsCntrId));
    });

    return dicEvent;
  }

  return (
    <div>
      {!initialized &&
        <div style={{position: 'relative', top: '-25px'}}>
          <SkeletonTheme color="#4287f5" highlightColor="#b9d2fa">
            <Skeleton count={1} height={2} />
          </SkeletonTheme>
        </div>
      }
      {!!mawb && initialized &&
        <div className='report-popup'>
          <MawbMilestoneInitializedImpl mawb={mawb} setMawb={setMawb} />

          <div className='report-footer'>
            <SecondaryButton
              size="regular"
              style={{width: '90px'}}
              onClick={save}>
              Update
            </SecondaryButton>
            <SecondaryButton
              size="regular"
              style={{width: '90px'}}
              onClick={onClose}>
              Cancel
            </SecondaryButton>
          </div>
        </div>
      }
    </div>
  );
}

function MawbMilestoneInitializedImpl({
  mawb,
  setMawb,
}: {
  mawb: AeMbl,
  setMawb: (x:Partial<AeMbl>) => void,
}) {
  return (
    <MawbMilestoneView
      mawb={mawb}
      onChange={setMawb}
    />
  );
}

const MawbMilestonePopup = ({
  mawbId,
  storageKey,
  onClose,
}:{
  mawbId: number,
  storageKey: string,
  onClose: VoidFunction,
}) => {
  return (
    <MawbMilestoneImpl mawbId={mawbId} key={storageKey} storageKey={storageKey} onClose={onClose} />
  );
};

MawbMilestonePopup.defaultProps = {
};

MawbMilestonePopup.propTypes = {
  mawbId: PropTypes.number.isRequired,
  storageKey: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default React.memo(MawbMilestonePopup);
